import { computed, inject, Injectable, signal } from '@angular/core';
import { EMPTY_FEATURE_COLLECTION } from '@daisi/models/feature-collection';
import { environment } from '@env/environment';
import { HandlingStatus } from '@generated/backend-api/model/handling-status';
import {
  DaisiDataSourceId,
  DaisiDataStreamerService,
} from '@services/stomp/daisi-data-streamer.service';
import { DaisiFeatureCollection } from '@models/daisi-feature-collection';
import { StreamingDataService } from '@services/data/streaming-data.service';
import { maxTimestamp, StalenessConfig } from '@services/data/last-update-age-monitor';

@Injectable({
  providedIn: 'root',
})
export class IncidentsDataService extends StreamingDataService<
  DaisiDataSourceId,
  DaisiFeatureCollection
> {
  protected readonly dataStreamer = inject(DaisiDataStreamerService);
  protected readonly STREAM_NAME = (environment.imStreamName ?? 'daisi') as DaisiDataSourceId;
  protected readonly sourceLabel = 'de incidentdata';

  readonly allFeatures = computed(() => this.state().features);
  readonly currentFeatures = computed(() => {
    return this.state().features.filter(
      (feature) =>
        feature.properties.handlingStatus === HandlingStatus.PENDING ||
        feature.properties.handlingStatus === HandlingStatus.IN_PROGRESS,
    );
  });
  readonly pendingFeatures = computed(() => {
    return this.state().features.filter(
      (feature) => feature.properties.handlingStatus === HandlingStatus.PENDING,
    );
  });
  readonly postponedFeatures = computed(() => {
    return this.state().features.filter(
      (feature) => feature.properties.handlingStatus === HandlingStatus.POSTPONED,
    );
  });
  readonly inProgressFeatures = computed(() => {
    return this.state().features.filter(
      (feature) => feature.properties.handlingStatus === HandlingStatus.IN_PROGRESS,
    );
  });
  readonly archivedFeatures = computed(() => {
    return this.state().features.filter(
      (feature) => feature.properties.handlingStatus === HandlingStatus.ARCHIVED,
    );
  });

  readonly visibleFeatures = signal<DaisiFeatureCollection>(
    EMPTY_FEATURE_COLLECTION as DaisiFeatureCollection,
  );
  readonly invisibleFeatures = computed(() => {
    return {
      type: 'FeatureCollection',
      features: this.state().features.filter(
        (feature) =>
          !this.visibleFeatures().features.find(
            (visibleFeature) => visibleFeature.properties.id === feature.properties.id,
          ),
      ),
    } as DaisiFeatureCollection;
  });

  protected readonly staleness: StalenessConfig = {
    label: 'incident',
    lastUpdatedTimestamp: () =>
      maxTimestamp(this.state().features, (feature) => feature.properties.lastUpdateTime),
    maxAgeMinutesDaytime: 3,
    maxAgeMinutesNighttime: 6,
  };
}
